import addPx from 'add-px-to-style';

import { translate, charsetEscape } from '../utils/translate';
import { log, logMethodCall } from '../utils/debug';
import Event from '../const/event';

export default class Widget {
  constructor(id, element, config, subscriptionManager, api, triggerEvent) {
    this.element = element;
    if (!this.element) {
      return;
    }
    this.config = config;
    this.subscriptionManager = subscriptionManager;
    this.isSubscribed = this.subscriptionManager ? this.subscriptionManager.subscribed : false;
    this.isSubscribedTargeting = false;
    this.api = api;
    this.triggerEvent = triggerEvent;
    this.showTopics = true;

    this.elementConfig = this.element.dataset || {};
    if (this.element.className === 'cleverpush-widget') {
      this.elementConfig = {
        id: this.elementConfig.id,
        ...(this.config.widgets.find((w) => w._id === this.elementConfig.id) || {}),
        type: 'widget'
      };
    }

    this.id = this.elementConfig.id || id;

    require('./widget.scss');

    this.channelTopics = (this.config.channelTopics || []).filter((topic) => {
      let show = true;
      if (show && topic.matchPath && topic.matchPath.length) {
        show = new RegExp(topic.matchPath).test(window.location.pathname);
      }
      if (show && topic.notMatchPath && topic.notMatchPath.length) {
        show = !(new RegExp(topic.notMatchPath).test(window.location.pathname));
      }
      return show;
    });

    this.facebookEnabled = false;
    if (this.config.facebookPageId && this.config.facebookPageId.length) {
      this.facebookEnabled = true;

      // generate an unique user reference
      this.facebookUserRef = this.subscriptionManager.storageManager.getFacebookUserRef();
      if (!this.facebookUserRef) {
        this.facebookUserRef = `${this.config.channelId}-`;
        this.facebookUserRef += Math.random().toString(36).slice(2);
      }
    }
  }

  trackShown() {
    try {
      const widgetViewed = parseInt(sessionStorage.getItem(`cleverpush-widget-viewed-${this.elementConfig.id}`), 10);
      const trackingAllowed = 1000 * 60 * 60 * 12; // 12 h
      if (isNaN(widgetViewed) || !widgetViewed || (widgetViewed + trackingAllowed < Date.now())) {
        this.api.trackWidget(this.elementConfig.id, 'confirm-alert');
        sessionStorage.setItem(`cleverpush-widget-viewed-${this.elementConfig.id}`, `${Date.now()}`);
      }
    } catch (e) {
      log.debug(e);
    }
  }

  objss(style = {}, opts = {}) {
    // modified from https://github.com/jxnblk/objss/blob/master/index.js

    const kebab = (str) => str.replace(/([A-Z])/g, (g) => `-${g.toLowerCase()}`);
    const px = (prop) => (val) => (typeof val === 'number' ? addPx(prop, val) : val);

    const toObj = (a) => (key) => ({
      key,
      prop: kebab(key),
      value: a[key]
    });
    const toArr = (obj) => Object.keys(obj).map(toObj(obj));

    const getParentKey = (key) => (/^:/.test(key)
      ? `&${key}`
      : /^@/.test(key)
        ? key
        : `& ${key}`);

    const formatNested = (values, opts) => (opts.newline
      ? `\n  ${values.split('\n').join('\n  ')}\n`
      : values);

    const ox = (style = {}, opts = {}) => toArr(style)
      .filter((s) => s.value !== null)
      .map((s) => (typeof s.value === 'object'
        ? ({
          parent: getParentKey(s.prop),
          value: ox(s.value, opts)
        })
        : s))
      .map(({
        parent, prop, key, value
      }) => (parent
        ? `${parent} { ${formatNested(value, opts)} }`
        : `${prop}: ${px(key)(value)};`))
      .join(opts.newline ? '\n' : '');

    return ox(style, opts);
  }

  getSelectedTopics() {
    logMethodCall('getSelectedTopics');

    let topics = (this.channelTopics || []).map((topic) => topic._id);

    if (this.element) {
      const checkboxes = this.element.querySelectorAll('input[name="topics[]"]');
      topics = [];
      for (let i = 0; i < checkboxes.length; i += 1) {
        if ((checkboxes[i].type === 'checkbox' || checkboxes[i].type === 'radio') && checkboxes[i].checked) {
          topics.push(checkboxes[i].value);
        }
      }
    }

    return topics;
  }

  getBrandingLinks() {
    const links = [];

    if (!this.config.hideBranding) {
      links.push(`<a href="https://cleverpush.com/" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">Powered by CleverPush</a>`);
    }
    if (this.config.privacyPolicyUrl) {
      links.push(`<a href="${this.config.privacyPolicyUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.privacyPolicyText || translate('privacy')}</a>`);
    }
    if (this.config.faqText && this.config.faqUrl) {
      links.push(`<a href="${this.config.faqUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.faqText || 'FAQ'}</a>`);
    }

    return links.join(' &nbsp;/&nbsp; ');
  }

  show(isSubscribedParam) {
    (async () => {
      this.isSubscribed = isSubscribedParam;
      this.isSubscribedTargeting = isSubscribedParam;

      if (this.isSubscribedTargeting && this.elementConfig.buttonOnlyForTagsTopics) {
        if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
          // eslint-disable-next-line no-restricted-syntax
          for (const tagId of this.elementConfig.tags) {
            // eslint-disable-next-line no-await-in-loop
            const hasTag = await this.subscriptionManager.storageManager.hasTag(tagId);
            if (!hasTag) {
              this.isSubscribedTargeting = false;
              break;
            }
          }
        }
        if (this.isSubscribedTargeting && this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
          const topicIds = await this.subscriptionManager.getTopics();
          // eslint-disable-next-line no-restricted-syntax
          for (const topicId of this.elementConfig.topics) {
            // eslint-disable-next-line no-await-in-loop
            const hasTopic = topicIds.includes(topicId);
            if (!hasTopic) {
              this.isSubscribedTargeting = false;
              break;
            }
          }
        }
      }

      const isSubscribed = this.elementConfig.buttonOnlyForTagsTopics ? this.isSubscribedTargeting : this.isSubscribed;

      this.element.id = `${this.element.className}-${this.id}`;

      if (this.element.className === 'cleverpush-widget') {
        try {
          if (localStorage.getItem(`cleverpush-widget-status-${this.elementConfig.id}`) === 'hidden') {
            return;
          }
        } catch (ignored) {}

        if (!this.elementConfig.showSubscribed && isSubscribed) {
          if (this.element) {
            this.element.style.display = 'none';
          }
          return;
        }
        if (this.element) {
          this.element.style.display = '';
        }
      }

      let setElementHtml;

      const initiateElements = () => {
        this.button = this.element.querySelector('.cleverpush-content-button-btn-subscribe, .cleverpush-widget-allow-button');
        if (this.button) {
          this.button.addEventListener('click', this.onClick.bind(this));

          if (this.element && this.element.dataset) {
            if (this.element.dataset.buttonBackgroundColor) {
              this.button.style.backgroundColor = this.element.dataset.buttonBackgroundColor;
            }
            if (this.element.dataset.buttonColor) {
              this.button.style.color = this.element.dataset.buttonColor;
            }
          }
        }

        this.denyButton = this.element.querySelector('.cleverpush-widget-deny-button');
        if (this.denyButton) {
          this.denyButton.addEventListener('click', this.onClickDeny.bind(this));
        }

        this.unsubscribeButton = this.element.querySelector('.cleverpush-content-button-btn-unsubscribe, .cleverpush-widget-unsubscribe-button');
        if (this.unsubscribeButton) {
          this.unsubscribeButton.addEventListener('click', this.onClickUnsubscribe.bind(this));

          if (this.element && this.element.dataset) {
            if (this.element.dataset.buttonBackgroundColor) {
              this.unsubscribeButton.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
            }
          }
        }

        this.topicSelection = this.element.querySelector('.cleverpush-content-button-topics');

        this.saveTopicsButton = this.element.querySelector('.cleverpush-content-button-save-topics-button');
        if (this.saveTopicsButton) {
          if (this.element.dataset.buttonBackgroundColor) {
            this.saveTopicsButton.style.backgroundColor = this.element.dataset.buttonBackgroundColor;
          }
          this.saveTopicsButton.addEventListener('click', this.saveTopics.bind(this));
        }

        this.reset(isSubscribed);
      };

      let button = `
  <div class="cleverpush-content-button-btn-wrap">
  <button class="cleverpush-content-button-btn cleverpush-content-button-btn-subscribe">${this.elementConfig.allowButtonText || this.elementConfig.buttonText || translate('bell.subscribe')}</button>
  <button class="cleverpush-content-button-btn cleverpush-content-button-btn-unsubscribe" title="${translate('unsubscribe.title')}" style="display: none;">&times;</button>
  </div>
  `;

      if (this.facebookEnabled) {
        if (window.CleverPush.browserType === 'facebook') {
          button = '';
        }

        if (this.config.facebookDoubleOptIn && !this.facebookDoubleOptInConfirmed) {
          // add the facebook confirm checkbox to topic selection (if double optin enabled)
          button += `
          <div style="margin-top: 15px; margin-bottom: 5px; font-weight: bold;" class="cleverpush-confirm-messenger-title">Facebook Messenger</div>
          <div class="cleverpush-topics cleverpush-confirm-topics"><div><label><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" id="cleverpush-facebook-checkbox"> Ich möchte den Facebook-Newsletter und akzeptiere die <a href="${this.config.privacyPolicyUrl}">Datenschutzerklärung</a></label></div></div>`;
        }

        // add the messenger button
        button += `<div class="fb-messenger-checkbox-wrap"><div class="fb-send-to-messenger" origin="${location.origin}" page_id="${this.config.facebookPageId}" messenger_app_id="${this.config.facebookAppId}" user_ref="${this.facebookUserRef}" allow_login="true" size="large" style="margin-top: 10px;"></div></div>`;
      }

      let topicSelection = '';
      if (this.channelTopics && this.channelTopics.length) {
        this.subscriptionManager.getTopics().then((selectedTopicIds) => {
          topicSelection += '<div class="cleverpush-topics cleverpush-content-button-topics">';
          this.channelTopics.filter((topic) => !topic.parentTopic).sort((a, b) => a.sort - b.sort).forEach((topic) => {
            const topicName = charsetEscape(topic.name);

            const childTopics = this.channelTopics.filter((childTopic) => childTopic.parentTopic === topic._id);

            topicSelection += `<div><label><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" name="topics[]" value="${topic._id}"${(!isSubscribed && !topic.defaultUnchecked) || (selectedTopicIds && selectedTopicIds.indexOf(topic._id) > -1) ? ' checked="checked"' : ''}> ${topicName}</label></div>`;

            if (childTopics.length) {
              childTopics.forEach((childTopic) => {
                const childTopicName = charsetEscape(childTopic.name);

                topicSelection += `<div class="topic-child" style="margin-left: 20px; font-size: 90%"><label><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" name="topics[]" value="${childTopic._id}" ${(!isSubscribed && !childTopic.defaultUnchecked) || (selectedTopicIds && selectedTopicIds.indexOf(childTopic._id) > -1) ? 'checked' : ''}> ${childTopicName}</label></div>`;
              });
            }
          });
          topicSelection += `<button type="button" class="cleverpush-content-button-save-topics-button cleverpush-content-button-btn" style="display: none;">${translate('panel.save')}</button>`;
          topicSelection += '</div>';

          setElementHtml();
        });
      }

      if (this.elementConfig.type === 'widget') {
        setElementHtml = () => {
          const widget = `
          ${this.elementConfig.showIcon ? (
        `<div class="cleverpush-widget-icon">
            ${this.elementConfig.iconUrl ? (`<img src="${this.elementConfig.iconUrl || ''}" style="max-width: 100%;" />`) : (`<img src="${'https://static.cleverpush.com/sdk/assets/bell.svg'}" style="opacity: 0.25; max-width: 100%;" />`)}
          </div>`
      ) : ''}
          <div class="cleverpush-widget-content">
            ${this.elementConfig.showContent ? (
        `<div class="cleverpush-widget-title" style="${this.objss(this.elementConfig.titleStyle)}">${this.elementConfig.title || ''}</div>`
      ) : ''}
            ${this.elementConfig.showContent ? (
        `<div class="cleverpush-widget-text">${this.elementConfig.text || ''}</div>`
      ) : ''}
            ${this.elementConfig.showTopics ? (
        `<div class="cleverpush-widget-text" style="margin-top: 15px; margin-bottom: 15px;">${topicSelection}</div>`
      ) : ''}
            <div class="cleverpush-widget-buttons">
              ${this.elementConfig.showDenyButton && !isSubscribed ? (
        `<button type="button" class="cleverpush-widget-deny-button">${this.elementConfig.denyButtonText || 'Ablehnen'}</button>`
      ) : ''}
              ${isSubscribed ? `
              <button type="button" class="cleverpush-widget-unsubscribe-button">${this.elementConfig.denyButtonText || this.elementConfig.unsubscribeButtonText || 'Push-Nachrichten deaktivieren'}</button>
              ` : `
              <button type="button" class="cleverpush-widget-allow-button">${this.elementConfig.allowButtonText || 'Akzeptieren'}</button>`}
            </div>
          </div>
          `;

          const style = `
          #${`cleverpush-widget-${this.elementConfig.id}`} { ${this.objss(this.elementConfig.style || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-content { ${this.objss(this.elementConfig.contentStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-title { ${this.objss(this.elementConfig.titleStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-buttons { ${this.objss(this.elementConfig.buttonsStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-icon { ${this.objss(this.elementConfig.iconStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-field { ${this.objss(this.elementConfig.fieldStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-field label { ${this.objss(this.elementConfig.labelStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-allow-button, #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-unsubscribe-button, #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-content-button-btn { ${this.objss(this.elementConfig.allowButtonStyle || {})} }
          #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-deny-button { ${this.objss(this.elementConfig.denyButtonStyle || {})} }
          `;

          if (!this.styleElem) {
            this.styleElem = document.createElement('style');
            document.head.appendChild(this.styleElem);
          }
          this.styleElem.innerHTML = `${style.replace(/"/g, '\\"').replace(/(\r\n|\n|\r)/gm, '')}`;

          this.element.innerHTML = widget;

          initiateElements();
        };
      } else if (this.elementConfig.type === 'box-simple' || this.elementConfig.type === 'box-count') {
        setElementHtml = () => {
          this.element.innerHTML = (
            `${`<div class="cleverpush-content-button-box cleverpush-content-button-${this.elementConfig.type}">`
            + '<div class="cleverpush-content-button-top">'
            + `  <div class="cleverpush-content-button-icon" style="background-image: url('${this.config.channelIcon}')"></div>`
            + '  <div class="cleverpush-content-button-body">'
            + `    <div class="cleverpush-content-button-title">${this.elementConfig.title || this.config.channelName}</div>`
            + `    <div class="cleverpush-content-button-text">${this.elementConfig.text || translate('confirm.info')}</div>`
            + '  </div>'
            + '</div>'
            + '<div class="cleverpush-content-button-bottom">'}${
              topicSelection
            }${button
            }  <div class="cleverpush-clearfix"></div>`
            + '</div>'
            + '<div class="cleverpush-content-button-branding">'
            + ` <span>
                ${this.getBrandingLinks()}
              </span>`
            + '</div>'
            + '</div>'
          );

          initiateElements();
        };
      } else {
        setElementHtml = () => {
          this.element.className += ' cleverpush-content-button-simple';
          this.element.innerHTML = button;

          initiateElements();
        };
      }

      setElementHtml();
    })();
  }

  reset(isSubscribed) {
    if (this.button) {
      if (isSubscribed) {
        this.button.className += ' disabled';
        this.button.innerHTML = this.elementConfig.denyButtonText || this.elementConfig.buttonTextSubscribed || (`✓&nbsp; ${translate('bell.subscribed')}`);
        if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
          this.button.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
        }
      } else {
        this.button.className.replace(' disabled', '');
        this.button.innerHTML = this.elementConfig.allowButtonText || this.elementConfig.buttonText || translate('bell.subscribe');
        if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
          this.button.style.removeProperty('border-color');
        }
      }
    }

    if (this.unsubscribeButton) {
      if (isSubscribed) {
        this.unsubscribeButton.style.display = '';
      } else {
        this.unsubscribeButton.style.display = 'none';
      }
    }

    if (this.topicSelection) {
      this.topicSelection.style.display = isSubscribed && !this.showTopics ? 'none' : '';
    }
    if (this.saveTopicsButton) {
      this.saveTopicsButton.style.display = isSubscribed ? '' : 'none';
    }
  }

  onTargetingClick() {
    const promises = [];

    if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
      promises.push(new Promise((resolve) => {
        this.subscriptionManager.getSubscriptionId().then((subscriptionId) => {
          Promise.all(this.elementConfig.tags.map((tagId) => new Promise((resolve) => {
            this.subscriptionManager.storageManager.getTags().then((storedTags) => {
              if (this.isSubscribedTargeting) {
                storedTags.splice(storedTags.indexOf(tagId), 1);
              } else {
                storedTags.push(tagId);
              }
              this.subscriptionManager.storageManager.setTags(storedTags);
              this.api[this.isSubscribedTargeting ? 'untagSubscription' : 'tagSubscription'](subscriptionId, tagId).then(() => {
                this.triggerEvent(Event.TAGS_SET);
                resolve();
              });
            });
          }))).then(resolve);
        });
      }));
    }

    if (this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
      promises.push(new Promise((resolve) => {
        this.subscriptionManager.getTopics().then((topics) => {
          const newTopics = [...topics];
          this.elementConfig.topics.forEach((topicId) => {
            const index = newTopics.findIndex((topic) => topic === topicId);
            if (this.isSubscribedTargeting && index >= 0) {
              newTopics.splice(index, 1);
            } else if (!this.isSubscribedTargeting && index < 0) {
              newTopics.push(topicId);
            }
          });
          this.subscriptionManager.setTopics(newTopics);
          this.subscriptionManager.sync().then(() => {
            this.triggerEvent(Event.TOPICS_SET);
            resolve();
          });
        });
      }));
    }

    return Promise.all(promises);
  }

  onClick(e) {
    e.preventDefault();

    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    if (this.isSubscribed && this.elementConfig.buttonOnlyForTagsTopics) {
      this.onTargetingClick();
      return;
    }

    if (this.isSubscribed) {
      this.subscriptionManager.unsubscribe(false).then(() => {

      });
    } else {
      // telegram
      if (this.config.multiChannels && this.config.multiChannels.telegramChannel && this.config.multiChannels.telegramChannel._id === this.elementConfig.channel && this.config.multiChannels.telegramChannel._id !== this.config.channelId) {
        window.open(`https://t.me/${this.config.multiChannels.telegramChannel.telegramUsername}`);
        return;
      }

      // facebook
      if (this.config.multiChannels && this.config.multiChannels.facebookChannel && this.config.multiChannels.facebookChannel._id === this.elementConfig.channel && this.config.multiChannels.facebookChannel._id !== this.config.channelId) {
        window.open(`https://m.me/${(this.config.multiChannels.facebookChannel.facebookPage || {}).id}`);
        return;
      }

      this.api.widgetId = this.id;
      this.api.confirmAlertShown();

      this.subscriptionManager.setTopics(this.getSelectedTopics());

      this.button.innerHTML = '<div class="cleverpush-loader"><div></div><div></div><div></div><div></div></div>';

      let confirmAlertSelectTopicsLaterOriginal;
      if (this.config.confirmAlertSelectTopicsLater) {
        confirmAlertSelectTopicsLaterOriginal = true;
        this.config.confirmAlertSelectTopicsLater = false;
      }

      // initial tags + topics
      if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
        if (this.subscriptionManager && this.subscriptionManager.initialTags) {
          this.elementConfig.tags.forEach((tagId) => {
            this.subscriptionManager.initialTags.push(tagId);
          });
        }
      }
      if (this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
        if (this.subscriptionManager && this.subscriptionManager.topics) {
          this.elementConfig.topics.forEach((topicId) => {
            this.subscriptionManager.topics.push(topicId);
          });
        }
      }

      this.subscriptionManager.subscribeForce(this.isSubscribed).then((permission) => {
        if (confirmAlertSelectTopicsLaterOriginal) {
          this.config.confirmAlertSelectTopicsLater = true;
        }
      }).catch((err) => {
        if (this.config.ownDomain && location.protocol === 'https:' && (err.reason === 'denied' || err.reason === 'default')) {
          if (this.subscriptionManager.confirm) {
            this.subscriptionManager.confirm.showBackdrop(undefined, 'denied', err.message);
          }
        } else {
          log.error(err);
        }
      });
    }
  }

  onClickDeny(e) {
    e.preventDefault();

    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    this.element.style.display = 'none';

    try {
      localStorage.setItem(`cleverpush-widget-status-${this.elementConfig.id}`, 'hidden');
    } catch (err) {}
  }

  onClickUnsubscribe(e) {
    e.preventDefault();

    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    if (this.isSubscribed && this.elementConfig.buttonOnlyForTagsTopics) {
      this.onTargetingClick();
      return;
    }

    this.subscriptionManager.unsubscribe(false).then(() => {

    });
  }

  saveTopics(e) {
    e.preventDefault();

    if (this.savingTopics) {
      return;
    }
    this.savingTopics = true;

    this.saveTopicsButton.innerHTML += '...';
    this.saveTopicsButton.disabled = true;
    if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
      this.saveTopicsButton.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
    }

    const resetButton = (success) => {
      if (success) {
        this.saveTopicsButton.innerHTML = `<span style="color: #009222">${translate('panel.successfullySaved')}</span>`;
        setTimeout(() => {
          this.saveTopicsButton.disabled = false;
          this.saveTopicsButton.innerHTML = translate('panel.save');
          this.savingTopics = false;
        }, 2000);
      } else {
        this.saveTopicsButton.disabled = false;
        this.saveTopicsButton.innerHTML = translate('panel.save');
        this.savingTopics = false;
      }
    };

    const topics = this.getSelectedTopics();

    this.subscriptionManager.setTopics(topics).then(() => {
      this.subscriptionManager.sync(false, topics).then(() => {
        resetButton(true);
      }).catch(() => {
        resetButton(false);
      });
    }).catch(() => {
      resetButton(false);
    });
  }
}
